/**
 * The workspace's right rail — context only, no primary actions.
 *
 * Cards, top to bottom: Client & commercials (identity + price / deadline
 * / billing-flag edit — admins can change these at every stage, started
 * included; every save syncs to the Backoffice project), Files (attached
 * docs + provenance), Backoffice (the
 * external project link — in-platform navigation lives on the case
 * chain), and Activity (collapsed by default; occasionally useful).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  Anchor,
  Button,
  Card,
  Checkbox,
  Collapse,
  Group,
  NumberInput,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { IconChevronDown, IconChevronRight, IconExternalLink } from '@tabler/icons-react';

import { BackofficeBehindNotice } from '@/components/BackofficeBehindNotice';
import { CommercialHistoryList, LastChangedLine } from '@/components/CommercialHistory';
import { ReasonDialog } from '@/components/ReasonDialog';
import { ReportRequestAttachments } from '@/components/ReportRequestAttachments';
import { config } from '@/config/environment';
import { useAuth } from '@/contexts/AuthContext';
import { useUkBankHolidays, type useReportRequestTriage } from '@/hooks';
import { useActorNames } from '@/hooks/useActorNames';
import { useCommercialChanges } from '@/hooks/useCommercialChanges';
import { vnextApiClient } from '@/services/vnext-api';
import { latestOf, needsReason } from '@/types/commercial-change';
import type { ReportConfigSummary } from '@/types/report-config';
import {
  formatRequestSource,
  ReportRequestDetail,
  triageStageLabel,
  type PricingUpdateRequest,
} from '@/types/report-request';
import {
  addBusinessDays,
  asNumberOrEmpty,
  currencySymbol,
  formatDueDate,
  toIsoDate,
} from '@/utils';

interface WorkspaceRailProps {
  detail: ReportRequestDetail;
  teamName: string | null;
  updatePricing: ReturnType<typeof useReportRequestTriage>['updatePricing'];
  /** The config the analyst just picked in Define — its default TAT and
   * price seed the commercials form (explicit Save still required). */
  configSeed?: ReportConfigSummary | null;
}

export function WorkspaceRail({
  detail,
  teamName,
  updatePricing,
  configSeed = null,
}: Readonly<WorkspaceRailProps>) {
  return (
    <Stack gap="md">
      <CommercialsCard
        detail={detail}
        teamName={teamName}
        updatePricing={updatePricing}
        configSeed={configSeed}
      />
      <ReportRequestAttachments reportRequestId={detail.id} />
      <BackofficeCard detail={detail} />
      <ActivityCard detail={detail} />
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Client & commercials
// ---------------------------------------------------------------------------

/** date-only input value ("YYYY-MM-DD") → end-of-day UTC instant, so a
 * report due "on the 12th" isn't overdue at 00:01 that morning. */
function dateToDueIso(date: string): string {
  return `${date}T23:59:59Z`;
}

const CURRENCY_OPTIONS = [
  { value: 'GBP', label: 'GBP £' },
  { value: 'EUR', label: 'EUR €' },
  { value: 'USD', label: 'USD $' },
];

const ACTIVATION_OPTIONS = [
  { value: 'required', label: 'Required' },
  { value: 'not_required', label: 'Not required' },
];

type DeadlineMode = 'turnaround' | 'due_date';

const DEADLINE_MODES = [
  { value: 'turnaround', label: 'Turnaround' },
  { value: 'due_date', label: 'Due date' },
];

/** The card's editable state — one object so re-sync, dirty-tracking and
 * the save payload each have a single seam. */
interface CommercialsDraft {
  price: number | '';
  currency: string | null;
  turnaround: number | '';
  dueDate: string;
  // The deadline is EITHER a turnaround (relative to Start) OR a fixed
  // date — a mode switch, not two inputs mirroring each other.
  mode: DeadlineMode;
  activation: string | null;
  expedited: boolean;
  poc: boolean;
  rnd: boolean;
  excluded: boolean;
}

function activationOption(value: boolean | null): string | null {
  if (value === null) {
    return null;
  }
  return value ? 'required' : 'not_required';
}

function draftFrom(detail: ReportRequestDetail): CommercialsDraft {
  return {
    price: detail.price ? Number(detail.price) : '',
    currency: detail.priceCurrency,
    turnaround: detail.turnaroundTimeDays ?? '',
    dueDate: detail.dueAtOverride?.slice(0, 10) ?? '',
    mode: detail.dueAtOverride ? 'due_date' : 'turnaround',
    activation: activationOption(detail.requiresClientActivationOverride),
    expedited: detail.expedited,
    poc: detail.isProofOfConcept,
    rnd: detail.isResearchAndDevelopment,
    excluded: detail.isExcludedFromBilling,
  };
}

/** The wire body for a draft. Full-state: every field rides along and
 * explicit nulls clear (the backend applies exactly the fields present).
 * Exactly one of turnaround / due_at is non-null — the inactive mode's
 * value is dropped. */
function payloadFrom(draft: CommercialsDraft): PricingUpdateRequest {
  const turnaround = draft.mode === 'turnaround' ? draft.turnaround : '';
  const dueDate = draft.mode === 'due_date' ? draft.dueDate : '';
  return {
    price: draft.price === '' ? null : String(draft.price),
    turnaround_time_days: turnaround === '' ? null : turnaround,
    expedited: draft.expedited,
    currency: (draft.currency as 'GBP' | 'EUR' | 'USD' | null) ?? null,
    due_at: dueDate ? dateToDueIso(dueDate) : null,
    requires_client_activation: draft.activation === null ? null : draft.activation === 'required',
    is_proof_of_concept: draft.poc,
    is_research_and_development: draft.rnd,
    is_excluded_from_billing: draft.excluded,
  };
}

/** The edit's body keys that are recorded in the history — and so need a reason
 * on a completed report — and the field name each is recorded under. */
const HISTORY_FIELD_FOR_KEY: Partial<Record<keyof PricingUpdateRequest, string>> = {
  price: 'price',
  currency: 'currency',
  turnaround_time_days: 'turnaround_time_days',
  due_at: 'due_date',
  is_proof_of_concept: 'is_proof_of_concept',
  is_research_and_development: 'is_research_and_development',
  is_excluded_from_billing: 'is_excluded_from_billing',
};

/** The recorded fields this draft would change, compared with what is saved. */
function changedHistoryFields(draft: CommercialsDraft, detail: ReportRequestDetail): string[] {
  const now = payloadFrom(draft);
  const saved = payloadFrom(draftFrom(detail));
  return Object.entries(HISTORY_FIELD_FOR_KEY)
    .filter(([key]) => {
      const k = key as keyof PricingUpdateRequest;
      return JSON.stringify(now[k]) !== JSON.stringify(saved[k]);
    })
    .map(([, field]) => field as string);
}

function useCommercialsDraft(detail: ReportRequestDetail, configSeed: ReportConfigSummary | null) {
  const [draft, setDraft] = useState<CommercialsDraft>(() => draftFrom(detail));
  const detailRef = useRef(detail);
  detailRef.current = detail;

  // Re-sync when the server values move (e.g. Define saved new numbers) —
  // keyed on the values, so a refetch that changes nothing keeps the edit.
  useEffect(() => {
    setDraft(draftFrom(detailRef.current));
  }, [
    detail.price,
    detail.priceCurrency,
    detail.turnaroundTimeDays,
    detail.dueAtOverride,
    detail.requiresClientActivationOverride,
    detail.expedited,
    detail.isProofOfConcept,
    detail.isResearchAndDevelopment,
    detail.isExcludedFromBilling,
  ]);

  // Define announces a config pick — pull its default commercials into
  // the form (the analyst still edits and saves explicitly). The due
  // field is left alone deliberately: it is an OVERRIDE, and writing it
  // here would silently pin the client due to a triage-time estimate.
  useEffect(() => {
    if (!configSeed) {
      return;
    }
    setDraft((current) => ({
      ...current,
      turnaround: configSeed.turnaroundTimeDays ?? '',
      price: configSeed.price != null ? Number(configSeed.price) : '',
    }));
  }, [configSeed]);

  const update = useCallback((patch: Partial<CommercialsDraft>) => {
    setDraft((current) => ({ ...current, ...patch }));
  }, []);

  // Dirty = the body we would send differs from the body the server state
  // would produce (a stored "1500.00" and a typed 1500 agree).
  const dirty =
    JSON.stringify(payloadFrom(draft)) !== JSON.stringify(payloadFrom(draftFrom(detail)));

  return { draft, update, dirty };
}

/** What the turnaround means in dates: once started the server-computed
 * due (started_at + N UK business days) is the truth for the saved
 * value; before Start the best we can show is "if started today". */
function turnaroundHint(
  draft: CommercialsDraft,
  detail: ReportRequestDetail,
  holidays: ReadonlySet<string>
): string {
  const turnaround = draft.mode === 'turnaround' ? draft.turnaround : '';
  if (typeof turnaround !== 'number' || turnaround <= 0) {
    return 'Counts from the report Start, whoever presses it';
  }
  if (detail.triageStage === 'started') {
    return detail.dueAt && turnaround === detail.turnaroundTimeDays
      ? `Due ${formatDueDate(detail.dueAt)}`
      : 'Recomputed from the Start date on save';
  }
  const due = toIsoDate(addBusinessDays(new Date(), turnaround, holidays));
  return `If started today: ${formatDueDate(`${due}T23:59:59Z`)}`;
}

interface DraftFieldProps {
  draft: CommercialsDraft;
  update: (patch: Partial<CommercialsDraft>) => void;
}

function DeadlineField({ draft, update, hint }: Readonly<DraftFieldProps & { hint: string }>) {
  return (
    <>
      <Stack gap={2}>
        <Text size="xs" fw={500}>
          Deadline
        </Text>
        <SegmentedControl
          size="xs"
          data={DEADLINE_MODES}
          value={draft.mode}
          onChange={(v) => update({ mode: v as DeadlineMode })}
          data-testid="rail-deadline-mode"
        />
      </Stack>
      {draft.mode === 'turnaround' ? (
        <NumberInput
          label="Turnaround (business days)"
          description={hint}
          size="xs"
          min={0}
          allowDecimal={false}
          value={draft.turnaround}
          onChange={(v) => update({ turnaround: asNumberOrEmpty(v) })}
        />
      ) : (
        <TextInput
          label="Due date"
          description="Fixed — stands regardless of when the report starts"
          size="xs"
          type="date"
          value={draft.dueDate}
          onChange={(e) => update({ dueDate: e.currentTarget.value })}
          data-testid="rail-due-date"
        />
      )}
    </>
  );
}

function BillingFlagsField({ draft, update }: Readonly<DraftFieldProps>) {
  return (
    <Stack gap={4}>
      <Text size="xs" fw={500}>
        Billing flags
      </Text>
      <Group gap="md">
        <Checkbox
          size="xs"
          label="POC"
          checked={draft.poc}
          onChange={(e) => update({ poc: e.currentTarget.checked })}
          data-testid="rail-flag-poc"
        />
        <Checkbox
          size="xs"
          label="R&amp;D"
          checked={draft.rnd}
          onChange={(e) => update({ rnd: e.currentTarget.checked })}
          data-testid="rail-flag-rnd"
        />
        <Checkbox
          size="xs"
          label="Exclude from billing"
          checked={draft.excluded}
          onChange={(e) => update({ excluded: e.currentTarget.checked })}
          data-testid="rail-flag-exclude"
        />
      </Group>
    </Stack>
  );
}

function CommercialsCard({
  detail,
  teamName,
  updatePricing,
  configSeed = null,
}: Readonly<WorkspaceRailProps>) {
  const holidays = useUkBankHolidays();
  const { draft, update, dirty } = useCommercialsDraft(detail, configSeed);
  const [submitting, setSubmitting] = useState(false);
  const started = detail.triageStage === 'started';
  // A completed report's figures may already have been billed: changing them
  // waits for a reason, asked for in a dialog that also says whether it was.
  const completed = detail.reportLifecycleStage === 'completed';
  const [fieldsAwaitingReason, setFieldsAwaitingReason] = useState<string[] | null>(null);
  const nameFor = useActorNames();
  const { changes, lastByField, refetch: refetchChanges } = useCommercialChanges(detail.reportId);

  const save = async (reason?: string) => {
    setSubmitting(true);
    try {
      const body = payloadFrom(draft);
      await updatePricing(reason ? { ...body, reason } : body);
      void refetchChanges();
    } finally {
      setSubmitting(false);
    }
  };

  const handleSave = async () => {
    const changed = changedHistoryFields(draft, detail);
    if (completed && needsReason(changed)) {
      setFieldsAwaitingReason(changed);
      return;
    }
    await save();
  };

  return (
    <Card shadow="sm" padding="md" radius="md">
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap" gap="xs">
          <Text fw={600} size="sm">
            Client &amp; commercials
          </Text>
          <BackofficeBehindNotice marker={detail.backofficeSyncMarker} what="price and due date" />
        </Group>
        <FactRow label="Client team" value={teamName ?? detail.teamId ?? '—'} />
        {detail.configName && detail.reportConfigId ? (
          <Group justify="space-between" wrap="nowrap" gap="xs">
            <Text size="xs" c="dimmed">
              Report config
            </Text>
            <Anchor
              component={Link}
              to={`/admin/report-configs/${detail.reportConfigId}`}
              size="xs"
              ta="right"
              data-testid="rail-config-link"
            >
              {detail.configName}
            </Anchor>
          </Group>
        ) : (
          <FactRow label="Report config" value={detail.configName ?? '—'} />
        )}
        {detail.requesterEmail && <FactRow label="Requester" value={detail.requesterEmail} />}
        <FactRow label="Source" value={formatRequestSource(detail.source)} />
        {detail.freshdeskTicketId && (
          <FactRow label="Freshdesk ticket" value={`#${detail.freshdeskTicketId}`} />
        )}
        {started && detail.dueAt && <FactRow label="Due" value={formatDueDate(detail.dueAt)} />}
        <Group grow>
          <NumberInput
            label="Price"
            size="xs"
            min={0}
            decimalScale={2}
            fixedDecimalScale
            prefix={currencySymbol(draft.currency ?? configSeed?.currency ?? detail.currency)}
            value={draft.price}
            onChange={(v) => update({ price: asNumberOrEmpty(v) })}
          />
          <Select
            label="Currency"
            size="xs"
            data={CURRENCY_OPTIONS}
            value={draft.currency}
            onChange={(currency) => update({ currency })}
            clearable
            placeholder={
              configSeed?.currency ? `Config default (${configSeed.currency})` : 'Config default'
            }
            data-testid="rail-currency"
          />
        </Group>
        <LastChangedLine label="Price" change={lastByField.price} nameFor={nameFor} />
        <DeadlineField
          draft={draft}
          update={update}
          hint={turnaroundHint(draft, detail, holidays)}
        />
        <LastChangedLine
          label="Deadline"
          change={latestOf(lastByField.due_date, lastByField.turnaround_time_days)}
          nameFor={nameFor}
        />
        <Select
          label="Client activation"
          size="xs"
          data={ACTIVATION_OPTIONS}
          value={draft.activation}
          onChange={(activation) => update({ activation })}
          clearable
          placeholder="Config default"
          data-testid="rail-activation"
        />
        <BillingFlagsField draft={draft} update={update} />
        <Button
          variant={draft.expedited ? 'filled' : 'default'}
          color="orange"
          onClick={() => update({ expedited: !draft.expedited })}
          size="xs"
        >
          {draft.expedited ? 'Expedited ✓' : 'Mark expedited'}
        </Button>
        <Button size="xs" onClick={handleSave} loading={submitting} disabled={!dirty}>
          Save pricing
        </Button>
        <CommercialHistoryList changes={changes} nameFor={nameFor} />
        <ReasonDialog
          opened={fieldsAwaitingReason !== null}
          reportId={detail.reportId}
          changedFields={fieldsAwaitingReason ?? []}
          submitting={submitting}
          onConfirm={async (reason) => {
            try {
              await save(reason);
            } catch {
              // The pricing hook has already told the person what went wrong.
            } finally {
              setFieldsAwaitingReason(null);
            }
          }}
          onCancel={() => setFieldsAwaitingReason(null)}
        />
      </Stack>
    </Card>
  );
}

function FactRow({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <Group justify="space-between" wrap="nowrap" gap="xs">
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text size="xs" ta="right" style={{ wordBreak: 'break-word' }}>
        {value}
      </Text>
    </Group>
  );
}

// ---------------------------------------------------------------------------
// Activity — readable stage history (labels, notes, actors)
// ---------------------------------------------------------------------------

function ActivityCard({ detail }: Readonly<{ detail: ReportRequestDetail }>) {
  const { user } = useAuth();
  // Occasionally useful, rarely needed — collapsed by default.
  const [open, setOpen] = useState(false);

  if (detail.history.length === 0) {
    return null;
  }

  // Newest first — the analyst cares about what just happened.
  const entries = [...detail.history].reverse();

  return (
    <Card shadow="sm" padding="md" radius="md">
      <Stack gap="xs">
        <UnstyledButton
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          data-testid="activity-toggle"
        >
          <Group gap={6}>
            {open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
            <Text fw={600} size="sm">
              Activity
            </Text>
            <Text size="xs" c="dimmed">
              {entries.length}
            </Text>
          </Group>
        </UnstyledButton>
        <Collapse in={open}>
          <Stack gap="xs">
            {entries.map((entry) => {
              const actor =
                entry.actorKind === 'system'
                  ? 'system'
                  : entry.actorUserId && user?.id != null && String(user.id) === entry.actorUserId
                    ? 'you'
                    : (entry.actorUserId ?? 'analyst');
              return (
                <Stack key={entry.id} gap={2}>
                  <Group justify="space-between" wrap="nowrap" gap="xs">
                    <Text size="xs">
                      {entry.fromStage ? `${triageStageLabel(entry.fromStage)} → ` : ''}
                      <strong>{triageStageLabel(entry.toStage)}</strong>
                    </Text>
                    <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                      {new Date(entry.occurredAt).toLocaleString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </Text>
                  </Group>
                  <Text size="xs" c="dimmed">
                    {actor}
                    {entry.notes ? ` — ${entry.notes}` : ''}
                  </Text>
                </Stack>
              );
            })}
          </Stack>
        </Collapse>
      </Stack>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Backoffice — the external project link. In-platform navigation (the
// enquiry, sibling requests, the report) lives on the case chain.
// ---------------------------------------------------------------------------

function BackofficeCard({ detail }: Readonly<{ detail: ReportRequestDetail }>) {
  const [backofficeProjectId, setBackofficeProjectId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!detail.reportId) {
      return;
    }
    void (async () => {
      try {
        const report = await vnextApiClient.getReport(detail.reportId as string);
        if (!cancelled) {
          setBackofficeProjectId(report.backoffice_project_id ?? null);
        }
      } catch {
        // Card simply doesn't render.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [detail.reportId]);

  if (!backofficeProjectId) {
    return null;
  }

  return (
    <Card shadow="sm" padding="md" radius="md">
      <Stack gap="xs">
        <Text fw={600} size="sm">
          Backoffice
        </Text>
        <Anchor
          href={`${config.backofficeAdminUrl}/admin/project/${backofficeProjectId}`}
          target="_blank"
          rel="noreferrer"
          size="xs"
          data-testid="backoffice-project-link"
        >
          <Group gap={4} wrap="nowrap">
            Project #{backofficeProjectId}
            <IconExternalLink size={12} />
          </Group>
        </Anchor>
      </Stack>
    </Card>
  );
}
